import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, CircleDashed, Loader2, Rocket, X } from 'lucide-react'
import type { CiDryRun, Release, ReleaseCheck, ReleaseList, ReleasePr } from '@one-deployment/api'
import { Badge, Button, Dialog } from '@/components/ui'
import { RoleGate } from '@/components/RoleGate'
import { ask } from '@/components/ask'
import { enc, get, post } from '@/lib/api'
import { qk } from '@/lib/query'
import { cn } from '@/lib/cn'
import { useCiRun } from '@/features/ci/Ci'
import { PipelineGraph, type PipelineNode } from '@/features/ci/Pipeline'

/** Merge into the base and ship to prod (features/24). Polled fast while one runs: the worker moves it a step a minute. */
export function useReleases(project: string) {
  return useQuery({
    queryKey: qk.projectReleases(project),
    queryFn: ({ signal }) => get<ReleaseList>(`/api/projects/${enc(project)}/releases`, signal),
    refetchInterval: (q) => (q.state.data?.current ? 5_000 : 60_000),
  })
}

const STEPS: { key: Release['step']; label: string; ship?: true }[] = [
  { key: 'merge', label: 'Merge PRs' },
  { key: 'checks', label: 'Checks', ship: true },
  { key: 'ship', label: 'Ship', ship: true },
  { key: 'watch', label: 'Prod pipeline', ship: true },
]

/** The two buttons in the git bar, and the progress panel they open. Leads and admins only; the click is the approval. */
/** One Push to prod button (merge → checks → prod workflow → Linear and Slack), like a project's own /push-main. */
export function ReleaseButtons({ project, base, prod, prs, shipCi = null, pushes = [] }: { project: string; base: string; prod: string; prs: number; shipCi?: string | null; pushes?: { env: string; base: string; prod: string }[] }) {
  const qc = useQueryClient()
  const { data } = useReleases(project)
  const [open, setOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const start = useMutation({
    mutationFn: (a: { kind: Release['kind']; env?: string }) => post<Release>(`/api/projects/${enc(project)}/releases`, a),
    onSuccess: () => { setError(null); setOpen(true); void qc.invalidateQueries({ queryKey: qk.projectReleases(project) }) },
    onError: (e) => setError(e instanceof Error ? e.message : String(e)),
  })
  const go = async (kind: Release['kind']) => {
    const ok = await ask(kind === 'merge'
      ? { title: `Merge into ${base}?`, body: `${prs} open PR${prs === 1 ? '' : 's'} into ${base} merge once their checks are green. Ticket PRs that are red or conflict go back to Claude first. This counts as your approval and is recorded under your name.`, confirm: `Merge into ${base}` }
      : { title: `Push ${base} to ${prod}?`, body: `The open PRs into ${base} merge, the checks run on ${base}, then ${base} goes to ${prod}. Uncommitted work in sessions and other branches is not included. This counts as your approval and is recorded under your name.`, confirm: `Ship to ${prod}`, danger: true })
    if (ok) start.mutate({ kind })
  }
  // flow.push: one button per environment; each brings the branch before it in through a pull request.
  const push = async (p: { env: string; base: string; prod: string }) => {
    const ok = await ask({ title: `Push ${p.base} to ${p.env}?`, body: `A pull request from ${p.base} into ${p.prod} opens and merges once its checks are green; ${p.env}'s pipeline then deploys from the merge. This counts as your approval and is recorded under your name.`, confirm: `Push to ${p.env}`, danger: p.env === 'prod' })
    if (ok) start.mutate({ kind: 'ship', env: p.env })
  }
  const cur = data?.current ?? null
  const shown = cur ?? data?.recent[0] ?? null
  return (
    <span className="ml-auto flex items-center gap-1.5">
      {error && <span className="max-w-64 truncate text-err" title={error}>{error}</span>}
      {shown && (
        <button onClick={() => setOpen(true)} title={cur ? 'Show progress' : `Last ${shown.kind === 'ship' ? 'push' : 'merge'}: ${shown.state}`} aria-label={cur ? 'Show progress' : `Last ${shown.kind === 'ship' ? 'push' : 'merge'}: ${shown.state}`}
          className={cn('flex h-7 items-center justify-center gap-1.5 rounded-md border text-xs hover:bg-hover', cur ? 'px-2.5' : 'w-7',
            cur ? 'border-info/40 bg-info/10 text-info' : shown.state === 'done' ? 'border-ok/30 bg-ok/10 text-ok' : shown.state === 'failed' ? 'border-err/30 bg-err/10 text-err' : 'border-border bg-surface-2 text-fg-muted')}>
          {cur
            ? <><Loader2 size={12} className="animate-spin" />{`${cur.kind === 'merge' ? 'Merging' : 'Shipping'}: ${STEPS.find((s) => s.key === cur.step)?.label ?? cur.step}`}</>
            : <StateIcon state={shown.state} />}
        </button>
      )}
      <RoleGate action="project.release" project={project}>
        {pushes.length ? pushes.map((p) => (
          <Button key={p.env} variant={p.env === 'prod' ? 'primary' : 'secondary'} disabled={!!cur || start.isPending} onClick={() => void push(p)} title={`Bring ${p.base} into ${p.prod} through a pull request that merges once its checks are green`}><Rocket size={12} />Push to {p.env}</Button>
        )) : <Button variant="primary" disabled={!!cur || start.isPending} onClick={() => void go('ship')} title={`Merge the open PRs into ${base}, run the checks, then ship ${base} to ${prod}`}><Rocket size={12} />Push to prod</Button>}
      </RoleGate>
      {shown && <ReleaseDialog project={project} release={shown} shipCi={shipCi} open={open} onOpenChange={setOpen} />}
    </span>
  )
}

function StateIcon({ state }: { state: Release['state'] }) {
  if (state === 'done') return <Check size={12} className="text-ok" />
  if (state === 'running') return <Loader2 size={12} className="animate-spin text-info" />
  return <X size={12} className={state === 'failed' ? 'text-err' : 'text-fg-faint'} />
}

const prTone: Record<ReleasePr['state'], 'muted' | 'ok' | 'warn' | 'info'> = { waiting: 'muted', fixing: 'info', merged: 'ok', left: 'warn' }
const checkTone: Record<ReleaseCheck['state'], 'muted' | 'ok' | 'err' | 'info'> = { pending: 'muted', running: 'info', passed: 'ok', failed: 'err', skipped: 'muted' }

function useStop(project: string, r: Release) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => post<Release>(`/api/projects/${enc(project)}/releases/${enc(r.id)}/cancel`),
    onSettled: () => void qc.invalidateQueries({ queryKey: qk.projectReleases(project) }),
  })
}

function ReleaseDialog({ project, release: r, shipCi, open, onOpenChange }: { project: string; release: Release; shipCi: string | null; open: boolean; onOpenChange: (o: boolean) => void }) {
  if (r.kind === 'ship' && (r.run?.ci || shipCi)) return <CiShipDialog project={project} release={r} shipCi={shipCi} open={open} onOpenChange={onOpenChange} />
  return <LegacyDialog project={project} release={r} open={open} onOpenChange={onOpenChange} />
}

/** A ship on our own CI: one graph, Claude's step first, then the run's jobs as ci-1 runs them. */
function CiShipDialog({ project, release: r, shipCi, open, onOpenChange }: { project: string; release: Release; shipCi: string | null; open: boolean; onOpenChange: (o: boolean) => void }) {
  const stop = useStop(project, r)
  const runId = r.run?.ci ?? null
  const run = useCiRun(project, runId ?? '', !!runId && open)
  // Before the run exists its jobs come from the file, so the whole pipeline shows from the first second.
  const plan = useQuery({
    queryKey: ['ci-plan', project, r.base, shipCi],
    queryFn: ({ signal }) => get<CiDryRun>(`/api/projects/${enc(project)}/ci/dry-run?branch=${encodeURIComponent(r.base)}&schedule=${encodeURIComponent(shipCi!)}`, signal),
    enabled: open && !runId && !!shipCi,
    staleTime: 5 * 60_000,
  })
  const CLAUDE = r.session ? 'Claude: merge PRs + checks' : 'Merge PRs + checks'
  const claude: PipelineNode = {
    name: CLAUDE, needs: [],
    status: runId ? 'success' : r.state === 'running' ? 'leased' : r.state === 'failed' ? 'failure' : r.state === 'cancelled' ? 'cancelled' : 'success',
    startedAt: r.startedAt,
    finishedAt: runId ? (run.data?.run.createdAt ?? null) : r.finishedAt,
    href: r.session?.id ? `/p/${enc(project)}/s/${enc(r.session.id)}` : undefined,
  }
  const jobs: PipelineNode[] = runId
    ? (run.data?.jobs ?? []).map((j) => ({ ...j, href: `/p/${enc(project)}/ci/${enc(runId)}` }))
    : (plan.data?.jobs ?? []).filter((j) => j.will !== 'skip').map((j) => ({ name: j.name, needs: j.needs, status: r.state === 'running' ? 'waiting' : 'skipped', startedAt: null, finishedAt: null }))
  const nodes = [claude, ...jobs.map((j) => (j.needs.length ? j : { ...j, needs: [CLAUDE] }))]
  const by = `${r.startedBy.name}${r.startedBy.githubLogin ? ` (@${r.startedBy.githubLogin})` : ''}`
  return (
    <Dialog open={open} onOpenChange={onOpenChange} width={1100}
      title={`Ship ${r.base} to ${r.prod}`}
      description={<span className="flex items-center gap-2">{by} · {new Date(r.startedAt).toLocaleString()}{r.sha && <span className="font-mono">· {r.sha.slice(0, 7)}</span>}
        <Badge tone={r.state === 'done' ? 'ok' : r.state === 'failed' ? 'err' : r.state === 'running' ? 'info' : 'muted'}>{r.state}</Badge>
        {runId && <a href={`/p/${enc(project)}/ci/${enc(runId)}`} className="text-info hover:underline">logs</a>}</span>}>
      {r.error && <p className="mb-3 rounded-md border border-err/40 bg-err/10 p-2 text-xs text-err">{r.error}</p>}
      <PipelineGraph nodes={nodes} />
      <div className="mt-3 flex items-center gap-2">
        <details className="flex-1 text-xs">
          <summary className="cursor-pointer text-fg-subtle">What happened</summary>
          <div className="mt-1 max-h-48 overflow-auto font-mono text-2xs">
            {r.events.map((e, i) => (
              <div key={i} className={cn(e.level === 'error' ? 'text-err' : e.level === 'warn' ? 'text-warn' : e.level === 'ok' ? 'text-ok' : 'text-fg-muted')}>
                <span className="text-fg-faint">{new Date(e.at).toLocaleTimeString()} </span>{e.text}
              </div>
            ))}
          </div>
        </details>
        {r.state === 'running' && (
          <RoleGate action="project.release" project={project}>
            <Button variant="danger" disabled={stop.isPending} onClick={() => void ask({ title: 'Stop it?', body: 'What already happened stays: merged PRs stay merged, and CI jobs already done stay done.', confirm: 'Stop', danger: true }).then((ok) => ok && stop.mutate())}>Stop</Button>
          </RoleGate>
        )}
      </div>
    </Dialog>
  )
}

function LegacyDialog({ project, release: r, open, onOpenChange }: { project: string; release: Release; open: boolean; onOpenChange: (o: boolean) => void }) {
  const stop = useStop(project, r)
  // A session release is Claude running the project's own command, then the prod workflow.
  const steps = r.session ? [{ key: 'merge' as const, label: 'Claude: PRs and checks' }, { key: 'watch' as const, label: 'Prod workflow' }] : STEPS.filter((s) => r.kind === 'ship' || !s.ship)
  const at = r.session && r.step === 'watch' && !r.run?.id ? 0 : steps.findIndex((s) => s.key === r.step)
  const by = `${r.startedBy.name}${r.startedBy.githubLogin ? ` (@${r.startedBy.githubLogin})` : ''}`
  return (
    <Dialog open={open} onOpenChange={onOpenChange} width={640}
      title={r.kind === 'merge' ? `Merge into ${r.base}` : `Ship ${r.base} to ${r.prod}`}
      description={`Started by ${by} at ${new Date(r.startedAt).toLocaleString()}${r.sha ? ` · ${r.base} at ${r.sha.slice(0, 7)}` : ''}`}>
      <ol className="mb-4 flex items-center gap-2 text-xs">
        {steps.map((s, i) => {
          const done = r.state === 'done' || (at >= 0 && i < at)
          const now = r.state === 'running' && i === at
          return (
            <li key={s.key} className={cn('flex items-center gap-1', now ? 'text-fg' : done ? 'text-ok' : 'text-fg-faint')}>
              {now ? <Loader2 size={12} className="animate-spin" /> : done ? <Check size={12} /> : <CircleDashed size={12} />}{s.label}
              {i < steps.length - 1 && <span className="text-fg-faint">→</span>}
            </li>
          )
        })}
        <Badge className="ml-auto" tone={r.state === 'done' ? 'ok' : r.state === 'failed' ? 'err' : r.state === 'running' ? 'info' : 'muted'}>{r.state}</Badge>
      </ol>
      {r.error && <p className="mb-3 rounded-md border border-err/40 bg-err/10 p-2 text-xs text-err">{r.error}</p>}

      {r.session && (
        <Section title="Claude">
          <p className="text-fg-subtle">Runs the project's own push command in a hidden session, on a fresh checkout of {r.base}: merges the PRs into {r.base}, runs the checks, starts the prod workflow.{' '}
            {r.session.id ? <a href={`/p/${enc(project)}/s/${enc(r.session.id)}`} className="text-info hover:underline">Open the session</a> : <span>Starting…</span>}</p>
        </Section>
      )}

      {!r.session && <Section title={`Pull requests into ${r.base}`}>
        {r.prs.length === 0 ? <p className="text-fg-subtle">None were open.</p> : r.prs.map((p) => (
          <div key={p.number} className="flex items-baseline gap-2">
            <Badge tone={prTone[p.state]}>{p.state}</Badge>
            <a href={p.url} target="_blank" rel="noreferrer" className="truncate hover:underline">#{p.number} {p.title}</a>
            {p.author && <span className="shrink-0 text-fg-faint">@{p.author}</span>}
            {p.why && <span className="text-fg-subtle">— {p.why}</span>}
          </div>
        ))}
      </Section>}

      {r.kind === 'ship' && !r.session && (
        <Section title="Checks">
          {r.checks.length === 0 ? <p className="text-fg-subtle">{r.step === 'merge' ? 'After the merges.' : 'None set (flow.ship.checks in the project file).'}</p> : r.checks.map((c) => (
            <div key={c.name}>
              <div className="flex items-baseline gap-2"><Badge tone={checkTone[c.state]}>{c.state}</Badge><span className="font-mono">{c.name}</span>{c.seconds !== null && <span className="text-fg-faint">{c.seconds}s</span>}</div>
              {c.tail && <pre className="mt-1 max-h-48 overflow-auto rounded-sm bg-surface-2 p-2 text-2xs whitespace-pre-wrap">{c.tail}</pre>}
            </div>
          ))}
        </Section>
      )}

      {r.run && (
        <Section title={<>Prod pipeline {r.run.ci ? <a href={`/p/${enc(project)}/ci/${enc(r.run.ci)}`} className="font-normal text-info hover:underline">on CI</a> : r.run.url && <a href={r.run.url} target="_blank" rel="noreferrer" className="font-normal text-info hover:underline">on GitHub</a>}</>}>
          {r.run.jobs.length === 0 ? <p className="text-fg-subtle">{r.session && !r.run.ci && r.step === 'watch' ? 'Claude is merging and checking; the pipeline starts when it is done…' : 'Waiting for it to start…'}</p> : r.run.jobs.map((j) => (
            <div key={j.name} className="flex items-baseline gap-2">
              <Badge tone={j.conclusion === 'success' ? 'ok' : j.conclusion === 'skipped' ? 'muted' : j.conclusion ? 'err' : 'info'}>{j.conclusion ?? j.status}</Badge>{j.name}
            </div>
          ))}
        </Section>
      )}

      <Section title="What happened">
        <div className="max-h-56 overflow-auto font-mono text-2xs">
          {r.events.map((e, i) => (
            <div key={i} className={cn(e.level === 'error' ? 'text-err' : e.level === 'warn' ? 'text-warn' : e.level === 'ok' ? 'text-ok' : 'text-fg-muted')}>
              <span className="text-fg-faint">{new Date(e.at).toLocaleTimeString()} </span>{e.text}
            </div>
          ))}
        </div>
      </Section>

      {r.state === 'running' && (
        <RoleGate action="project.release" project={project}>
          <div className="flex justify-end">
            <Button variant="danger" disabled={stop.isPending} onClick={() => void ask({ title: 'Stop it?', body: 'What already happened stays: merged PRs stay merged, and a prod workflow already started keeps running on GitHub.', confirm: 'Stop', danger: true }).then((ok) => ok && stop.mutate())}>Stop</Button>
          </div>
        </RoleGate>
      )}
    </Dialog>
  )
}

function Section({ title, children }: { title: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="mb-3 text-xs">
      <h3 className="mb-1 font-semibold text-fg-muted">{title}</h3>
      <div className="space-y-1">{children}</div>
    </section>
  )
}
